/**
 * Early `beforeinstallprompt` capture (ADR-0018).
 *
 * Chromium fires `beforeinstallprompt` ONCE, early in the page load (typically while the user is still
 * on `/login`), and never re-fires it on client-side SPA navigation. The install button, however, lives
 * deep in the authenticated family shell (`InstallAffordance` in `AppShell`) — which mounts long after
 * that event. If we only listened from inside that component, we'd miss the event every time and the
 * button would never appear (on ANY device). So we attach the listener at app startup (module scope,
 * from `main.tsx`) and stash the deferred event here; the component reads it via `useInstallPrompt`
 * whenever it eventually mounts. We still `preventDefault()` so Chromium's own mini-infobar stays
 * suppressed and we drive our own button (a child's session is never interrupted by a browser popup).
 */

export interface BeforeInstallPromptEvent extends Event {
  readonly prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let started = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * Begin capturing install-related events. Call ONCE, as early as possible (app entry) — before any
 * component that renders the install affordance mounts. Idempotent + SSR-safe.
 */
export function initInstallCapture(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('beforeinstallprompt', (event: Event) => {
    event.preventDefault(); // suppress Chromium's mini-infobar; we drive a custom button
    deferred = event as BeforeInstallPromptEvent;
    emit();
  });
  // Once installed the deferred prompt is spent and must not be offered again.
  window.addEventListener('appinstalled', () => {
    deferred = null;
    emit();
  });
}

/** The captured, not-yet-consumed install prompt (or null). Stable identity until it changes. */
export function getDeferredPrompt(): BeforeInstallPromptEvent | null {
  return deferred;
}

/** Drop the deferred prompt after it's been shown (single-use) or the app was installed. */
export function clearDeferredPrompt(): void {
  if (deferred === null) return;
  deferred = null;
  emit();
}

/** Subscribe to capture/clear changes (for `useSyncExternalStore`). Returns an unsubscribe. */
export function subscribeInstall(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}
